var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import ReactDOM from 'react-dom';
import { createSubcomponent, createElemPropsHook, useWindowSize, useForkRef, getCanvasTheme, } from '@workday/canvas-kit-react/common';
import { usePopupModel, usePopupStack } from '@workday/canvas-kit-react/popup';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@workday/canvas-kit-react/layout';
import { useModalModel } from './hooks';
var fadeIn = keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  from {\n    background: none;\n  }\n  to {\n    background: rgba(0,0,0,0.65);\n  }\n"], ["\n  from {\n    background: none;\n  }\n  to {\n    background: rgba(0,0,0,0.65);\n  }\n"])));
var Container = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.65)',
    animationName: "" + fadeIn,
    animationDuration: '0.3s',
    // Allow overriding of animation in special cases
    '.wd-no-animation &': {
        animation: 'none',
    },
});
// This centering container helps fix an issue with Chrome. Chrome doesn't normally do subpixel
// positioning, but seems to when using flexbox centering. This messes up Popper calculations inside
// the Modal. The centering container forces a "center" pixel calculation by making sure the width
// is always an even number
var ResponsiveContainer = styled('div')(function (_a) {
    var _b;
    var theme = _a.theme;
    var canvasTheme = getCanvasTheme(theme);
    return _b = {
            maxHeight: '100vh',
            display: 'flex',
            position: 'absolute',
            left: 0,
            top: 0,
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh'
        },
        _b[canvasTheme.breakpoints.down('s')] = {
            alignItems: 'end',
        },
        _b;
});
export var ModalOverlay = createSubcomponent('div')({
    displayName: 'Modal.Overlay',
    modelHook: useModalModel,
})(function (elemProps, Element, model) {
    return model.state.visibility !== 'hidden' ? (React.createElement(OpenModalOverlay, __assign({ as: Element, model: model }, elemProps))) : null;
});
export var useModalOverlay = createElemPropsHook(usePopupModel)(function (_a, ref) {
    var state = _a.state;
    var elementRef = useForkRef(ref, state.stackRef);
    usePopupStack(elementRef);
    return {
        // The ref should not be applied to an element. The passed ref will apply to the stackRef instead
        ref: undefined,
    };
});
var OpenModalOverlay = createSubcomponent('div')({
    displayName: 'Modal.OpenOverlay',
    modelHook: useModalModel,
    elemPropsHook: useModalOverlay,
})(function (elemProps, Element, model) {
    var windowSize = useWindowSize();
    var content = (React.createElement(Container, __assign({ as: Element }, elemProps),
        React.createElement(ResponsiveContainer
        // make sure the centering container is an even number of pixels to avoid sub-pixel
        // inaccuracies due to centering
        , { 
            // make sure the centering container is an even number of pixels to avoid sub-pixel
            // inaccuracies due to centering
            style: {
                width: windowSize.width % 2 === 1 ? 'calc(100vw - 1px)' : '100vw',
            } }, elemProps.children)));
    // only render something on the client
    if (typeof window !== 'undefined') {
        return ReactDOM.createPortal(content, model.state.stackRef.current);
    }
    else {
        return null;
    }
});
var templateObject_1;
